import { useState } from "react";
import { CheckCircle2, AlertTriangle, X, ShieldCheck, Loader2, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { formatDate } from "../lib/utils-format";

const VERDICT_STYLE = {
  "GO": { cls: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30", Icon: CheckCircle2 },
  "NO-GO": { cls: "text-red-600 bg-red-500/10 border-red-500/30", Icon: X },
  "CAUTION": { cls: "text-amber-600 bg-amber-500/10 border-amber-500/30", Icon: AlertTriangle },
};

export function AnalysisResults({ analysis, analyzedAt, loading, onReanalyze, busy }) {
  const [open, setOpen] = useState(false);
  const v = (analysis?.verdict || "").toUpperCase();
  const { cls, Icon } = VERDICT_STYLE[v] || VERDICT_STYLE["CAUTION"];

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="analysis-results-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-6 py-3.5 border-b border-border bg-secondary/30 flex items-center justify-between gap-3 text-left hover:bg-secondary/50 transition-colors" data-testid="analysis-results-toggle">
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="text-sm font-semibold">Analysis Results</span>
          {analysis?.verdict && !open && (
            <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`}>{analysis.verdict}</span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (loading ? (
        <div className="flex items-center gap-3 py-10 justify-center text-sm text-muted-foreground" data-testid="analysis-results-loading">
          <Loader2 className="h-5 w-5 animate-spin" />Angela is reviewing the numbers…
        </div>
      ) : analysis ? (
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${cls}`} data-testid="analysis-results-verdict">
              <Icon className="h-6 w-6 shrink-0" />
              <div>
                <div className="text-lg font-bold leading-none">{analysis.verdict}</div>
                {analysis.confidence && <div className="text-xs opacity-80 mt-1">{analysis.confidence} confidence</div>}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={onReanalyze} disabled={busy} data-testid="analysis-results-rerun-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}Re-analyze
            </Button>
          </div>

          {analysis.recommended_strategy && analysis.recommended_strategy !== "None" && (
            <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2.5" data-testid="analysis-results-recommended-strategy">
              <span className="text-[11px] uppercase tracking-wide text-primary font-semibold">Recommended Strategy</span>
              <span className="text-sm font-bold text-foreground">{analysis.recommended_strategy}</span>
            </div>
          )}

          {analysis.headline && <p className="text-sm font-medium">{analysis.headline}</p>}
          {analysis.summary && <p className="text-sm text-muted-foreground leading-relaxed">{analysis.summary}</p>}

          {analysis.strengths?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-1.5">Strengths</div>
              <ul className="space-y-1">
                {analysis.strengths.map((s, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-emerald-500">+</span>{s}</li>)}
              </ul>
            </div>
          )}
          {analysis.concerns?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-1.5">Concerns</div>
              <ul className="space-y-1">
                {analysis.concerns.map((s, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-amber-500">!</span>{s}</li>)}
              </ul>
            </div>
          )}
          {analysis.missing_data?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Missing Data</div>
              <ul className="space-y-1">
                {analysis.missing_data.map((s, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="opacity-50">•</span>{s}</li>)}
              </ul>
            </div>
          )}
          {analysis.recommendation && (
            <div className="rounded-lg bg-secondary/50 border border-border px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide mb-1">Recommendation</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{analysis.recommendation}</p>
            </div>
          )}
          {analyzedAt && <p className="text-[11px] text-muted-foreground/70">Analyzed {formatDate(analyzedAt)}</p>}
        </div>
      ) : (
        <div className="px-6 py-6 space-y-3" data-testid="analysis-results-empty">
          <p className="text-sm text-muted-foreground">Run Angela to get an internal GO / CAUTION / NO-GO verdict on this deal.</p>
          <Button size="sm" variant="outline" onClick={onReanalyze} disabled={busy} data-testid="analysis-results-run-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}Analyze
          </Button>
        </div>
      ))}
    </div>
  );
}
